/** @format */

import React, {useContext, useEffect, useState} from 'react';

import {QuoteContext} from 'frontier-quoter';
import {Input, GridSelect, Select} from '@sttark/design-system';
import {Header} from '../header/header';

import {
    GRAPHIC_DESIGN_COLOR_TYPES,
    GRAPHIC_DESIGN_STYLES,
    DESIGN_QUOTER_STEP_QUOTER,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_DESIGN_STYLE,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_COLOR_PREFERENCES,
    DESIGN_QUOTER_STEP_DESIGN_DIRECTION_BRAND_AND_PRODUCT,
    PRODUCT_TYPE_LABEL
} from 'frontier-constants';

import GoBack from './go_back';
import AssetsUploader from 'frontier/ds/quoter/design_quoter/assets_uploader';
import update from 'immutability-helper';
import Section from 'frontier/ds/quoter/section';
import {formatMoney} from 'frontier/ds/quoter/functions/format';
import OrderSummary from 'frontier/ds/quoter/design_quoter/order_summary';

export default function DesignDirection() {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;

    const stepComponents = {
        [DESIGN_QUOTER_STEP_DESIGN_DIRECTION_DESIGN_STYLE]: <DesignDirectionDesignStyle />,
        [DESIGN_QUOTER_STEP_DESIGN_DIRECTION_BRAND_AND_PRODUCT]: <DesignDirectionBrandAndProduct />,
        [DESIGN_QUOTER_STEP_DESIGN_DIRECTION_COLOR_PREFERENCES]: <DesignDirectionColorPreferences />
    };

    return (
        <div className="container mt-3 mb-8">
            <Header />
            <div className="row g-lg-7">
                <div className="col-lg-8">
                    <GoBack
                        label={`Back to ${ctx.singularProduct} selection`}
                        onClick={() => ctx.handleSetDesignQuoterStep(DESIGN_QUOTER_STEP_QUOTER)}
                    />
                    <h1 className="mt-0">Design Direction</h1>
                    {stepComponents[ctx.designQuoterStep]}
                </div>
                <div className="col-lg-4">
                    <div className=" pt-5 sticky-top">
                        <h4 className="mt-0">Order Summary</h4>
                        <OrderSummary />
                    </div>
                </div>
            </div>
        </div>
    );
}

const initialState = {
    design_style_preferences: [],
    color_preferences: []
};

function mapOrderToState(prev, designOrder) {
    const styles = designOrder.design_style_preferences;
    const colors = designOrder.color_preferences;

    // Handle old format conversion to new format
    let stylePreferences = [];
    if (styles) {
        if (Array.isArray(styles)) {
            // Already in new format
            stylePreferences = styles;
        } else if (styles.primary !== undefined && styles.secondary !== undefined) {
            // Convert old format to new format
            const secondary = Array.isArray(styles.secondary)
                ? styles.secondary
                : [styles.secondary];
            secondary.forEach(secondaryStyle => {
                if (secondaryStyle) {
                    stylePreferences.push({
                        primary: styles.primary,
                        secondary: secondaryStyle
                    });
                }
            });
        }
    }

    return {
        ...prev,
        design_style_preferences: stylePreferences,
        color_preferences: colors ? colors : []
    };
}

function DesignDirectionDesignStyle() {
    const ctx = useContext(QuoteContext);
    const {designOrder} = ctx;

    const [state, setState] = useState(mapOrderToState(initialState, designOrder));
    const [selectedCategory, setSelectedCategory] = useState('');
    useEffect(() => {
        ctx.handleDesignOrderChange({$merge: state});
    }, [state]);

    const handleCategoryChange = value => {
        setSelectedCategory(value);
    };

    // Use useRef to keep track of the latest state to avoid closure issues
    const stateRef = React.useRef(state);

    // Update the ref whenever state changes
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const handleStylesChange = selectedStyleValues => {
        // Ensure selectedStyleValues is always an array
        const styleValues = Array.isArray(selectedStyleValues) ? selectedStyleValues : [];

        // Convert selectedCategory to number for consistent comparison
        const categoryId = parseInt(selectedCategory);

        // Use the current state from the ref to avoid stale closures
        const currentState = stateRef.current;

        // Get styles not in this category - these remain unchanged
        const stylesNotInThisCategory = currentState.design_style_preferences.filter(
            style => style.primary !== categoryId
        );

        // Create new styles for the selected category
        const newCategoryStyles = [];

        // Process each selected style value
        styleValues.forEach(styleValue => {
            // Create a new object for each style (don't modify existing objects)
            newCategoryStyles.push({
                primary: categoryId,
                secondary: parseInt(styleValue)
            });
        });

        // Combine all styles
        const newStyles = [...stylesNotInThisCategory, ...newCategoryStyles];

        // Limit to maximum of 3 total styles
        const limitedStyles = newStyles.slice(0, 3);

        // Use a callback function to ensure we're working with the latest state
        setState(prevState => {
            // Create a completely new object to ensure React detects the change
            return {
                ...prevState,
                design_style_preferences: limitedStyles
            };
        });
    };

    // Get the count of total selections
    const totalSelections = stateRef.current.design_style_preferences.length;
    const remainingSelections = 3 - totalSelections;

    // Track the current state of selections
    return (
        <>
            <h3>Select Your Design Style</h3>
            <p>
                Let us know which designs match your style! You can choose up to 3 designs across
                different categories. Selecting which designs you like will help our design team get
                a good feel for your brand style.
            </p>
            <p className="tw-font-bold">
                You can select up to 3 styles total across all categories.
                {remainingSelections > 0
                    ? ` You can select ${remainingSelections} more style${
                          remainingSelections !== 1 ? 's' : ''
                      }.`
                    : ' You have selected the maximum of 3 styles.'}
            </p>
            <Select
                label="Choose a Category"
                options={GRAPHIC_DESIGN_STYLES.map(s => ({label: s.label, value: s.id}))}
                value={selectedCategory}
                onChange={handleCategoryChange}
            />
            <div className="mt-6">
                {selectedCategory && (
                    <GridSelect
                        multiple={3} // Allow up to 3 selections per category
                        ratio="16x9"
                        options={GRAPHIC_DESIGN_STYLES.map(s => ({
                            label: s.label,
                            value: s.id,
                            image:
                                s.image +
                                (designOrder.design_product_type_id === PRODUCT_TYPE_LABEL
                                    ? 'label/'
                                    : 'carton/') +
                                GRAPHIC_DESIGN_STYLES.find(
                                    type => type.id === parseInt(selectedCategory)
                                ).label +
                                '-' +
                                s.label +
                                '.png'
                        }))}
                        maxCols={3}
                        value={state.design_style_preferences
                            .filter(style => style.primary === parseInt(selectedCategory))
                            .map(style => style.secondary)}
                        onChange={handleStylesChange}
                    />
                )}
            </div>
            <div className="mt-4">
                <h4>Your Selected Styles:</h4>
                {state.design_style_preferences.length > 0 ? (
                    <ul className="tw-list-disc tw-pl-5">
                        {state.design_style_preferences.map((preference, idx) => {
                            const primaryStyle = GRAPHIC_DESIGN_STYLES.find(
                                type => type.id === preference.primary
                            );
                            const secondaryStyle = GRAPHIC_DESIGN_STYLES.find(
                                type => type.id === preference.secondary
                            );
                            return (
                                <li key={idx} className="tw-mb-2">
                                    {primaryStyle.label}/{secondaryStyle.label}
                                    <button
                                        className="tw-ml-2 tw-text-red-500 tw-bg-transparent tw-border-0"
                                        onClick={() => {
                                            // Use the current state from the ref to avoid stale closures
                                            const currentState = stateRef.current;
                                            const updatedPreferences = [
                                                ...currentState.design_style_preferences
                                            ];
                                            updatedPreferences.splice(idx, 1);

                                            // Use a callback to ensure we're working with the latest state
                                            setState(prevState => {
                                                return {
                                                    ...prevState,
                                                    design_style_preferences: updatedPreferences
                                                };
                                            });
                                        }}>
                                        Remove
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p>No styles selected yet.</p>
                )}
            </div>
            <h3>Upload Your Own Style References</h3>
            <AssetsUploader uploadType="style_reference" />
        </>
    );
}

function DesignDirectionColorPreferences() {
    const ctx = useContext(QuoteContext);
    const {designOrder} = ctx;

    return (
        <>
            <h3>Select Your Color Preferences</h3>
            <p>
                Choose up to 3 color palettes you'd like us to explore in your design OR specify
                your brand colors below.
            </p>
            <div className="mt-6">
                <GridSelect
                    multiple={3}
                    options={GRAPHIC_DESIGN_COLOR_TYPES.map(s => ({
                        label: s.label,
                        value: s.id,
                        description: s.description,
                        image: s.image
                    }))}
                    ratio="21x9"
                    maxCols={4}
                    value={designOrder.color_preferences}
                    onChange={a => ctx.handleDesignOrderChange({color_preferences: {$set: a}})}>
                    {({option, selected, handleClick}) => (
                        <div className="vstack h-100">
                            <h6>{option.label}</h6>
                            <p className="flex-grow-1">{option.description}</p>
                            <button
                                className="grid-select--button border-all w-100"
                                aria-pressed={selected}
                                style={{height: 60, overflow: 'hidden'}}
                                onClick={() => handleClick(option.value)}>
                                <span className="d-flex p-1 align-items-center justify-content-center">
                                    <img
                                        alt={option.label}
                                        src={option.image}
                                        style={{maxWidth: '100%', maxHeight: '100%'}}
                                    />
                                </span>
                            </button>
                        </div>
                    )}
                </GridSelect>
            </div>
            <h5>I have specific brand colors I'd like to use.</h5>
            <p>
                Please specify your brand colors using their CMYK values or Pantone colors. Pantone
                colors will be matched as closely as possible using CMYK color builds. (If RGB & hex
                colors are specified instead of CMYK or Pantone, they will be converted to CMYK
                before printing. Slight color variation may occur.)
            </p>
            <Input
                rows={3}
                label="Brand colors"
                value={designOrder.color_preference_specific || ''}
                onChange={v => ctx.handleDesignOrderChange({color_preference_specific: {$set: v}})}
            />
        </>
    );
}

function DesignDirectionBrandAndProduct() {
    const ctx = useContext(QuoteContext);
    const {designOrder} = ctx;

    return (
        <div className="row">
            <div className="col-lg-12">
                <h3>Describe Your Brand and Product</h3>
                <p>
                    Include all of your great ideas so our designers know what you're looking for.
                </p>
                <Input
                    rows={6}
                    label="Describe your brand/company to us in 2-3 sentences*"
                    value={designOrder.brand_description || ''}
                    onChange={v => ctx.handleDesignOrderChange({brand_description: {$set: v}})}
                />
                <Input
                    label="Website URL*"
                    value={designOrder.website_url || ''}
                    onChange={v => ctx.handleDesignOrderChange({website_url: {$set: v}})}
                />
                <p>
                    Include the name of your product as it should appear on the{' '}
                    {ctx.singularProduct} and describe what the product is in a few words/sentences.
                </p>
                <Input
                    rows={6}
                    label="Product Name and Description*"
                    value={designOrder.product_name_description || ''}
                    onChange={v =>
                        ctx.handleDesignOrderChange({product_name_description: {$set: v}})
                    }
                />
                <Input
                    rows={6}
                    required
                    label="Ingredients and Directions for Use*"
                    value={designOrder.product_ingredients_directions || ''}
                    onChange={v =>
                        ctx.handleDesignOrderChange({product_ingredients_directions: {$set: v}})
                    }
                />
                <h5>Fonts</h5>
                <p>
                    Do you have specific fonts you'd like us to use? Please note that there may be
                    an additional cost associated with purchasing a font license for your project.
                </p>
                <Input
                    rows={6}
                    label="Provide a list of any specific fonts you would like us to use and where in your design you would like them used.*"
                    placeholder="Provide a list of any specific fonts you would like us to use and where in your design you would like them used.*"
                    value={designOrder.specific_fonts || ''}
                    onChange={v => ctx.handleDesignOrderChange({specific_fonts: {$set: v}})}
                />

                <h3>Creative Vision</h3>
                <p>
                    Describe what you want your {ctx.singularProduct} to look like. The more detail
                    you provide, the better. This can include details about where you want your
                    ingredients list, barcode information, imagery, etc. to go on your design.
                </p>
                <Input
                    rows={6}
                    label={
                        designOrder.design_product_type_id === PRODUCT_TYPE_LABEL
                            ? 'Example: I would like the ingredients on the side, the barcode on the back, the logo centered…*'
                            : 'Example: I would like the ingredients on the right panel, the barcode on the back panel, and the logo on the top flap and front panel…*'
                    }
                    value={designOrder.look_feel_description || ''}
                    onChange={v => ctx.handleDesignOrderChange({look_feel_description: {$set: v}})}
                />
                <p>
                    Any additional text on the {ctx.singularProduct}? The more detail you provide,
                    the better.
                </p>
                <Input
                    rows={6}
                    label={`Additional text on ${ctx.singularProduct}`}
                    value={designOrder.additional_text || ''}
                    onChange={v => ctx.handleDesignOrderChange({additional_text: {$set: v}})}
                />

                <h5>Additional Files</h5>
                <p>
                    Do you have any other files that may be helpful for our designers? Example:
                    brand guidelines, sketches, barcode/UPC, product spec sheets, photos of your
                    product, labels you may like, etc.
                </p>
                <AssetsUploader uploadType="creative_vision" />
            </div>
        </div>
    );
}
